import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import {
  SERVICE_GROUPS,
  DETAILED_DESCRIPTIONS,
} from "@/data/site";
import PayPalButton from "@/components/PayPalButton";
import ReassuranceLine from "@/components/ReassuranceLine";
import LegalDisclosure from "@/components/LegalDisclosure";
import { FADE_INITIAL, FADE_IN_VIEW, VIEWPORT_ONCE } from "@/lib/motion";

function ServiceCard({ service, index }) {
  return (
    <motion.div
      initial={FADE_INITIAL}
      whileInView={FADE_IN_VIEW}
      viewport={VIEWPORT_ONCE}
      transition={{ duration: 0.45, delay: (index % 3) * 0.06 }}
      data-testid={`service-card-${service.id}`}
      className="flex flex-col rounded-lg border border-slate-200 bg-white p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg"
    >
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-serif text-xl font-semibold leading-snug text-[#0B1B3D]">
          {service.title}
        </h3>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-2xl font-bold text-[#0B1B3D]">{service.price}</span>
        {service.note && (
          <span className="rounded-full bg-[#D4AF37]/15 px-2.5 py-0.5 text-xs font-semibold text-[#8a6d1a]">
            {service.note}
          </span>
        )}
      </div>
      <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-600">{service.description}</p>
      <div className="mt-7">
        <PayPalButton
          serviceTitle={service.title}
          price={service.price}
          paypalLink={service.paypalLink}
          testId={`buy-now-${service.id}`}
        />
      </div>
    </motion.div>
  );
}

export default function Services() {
  return (
    <div className="bg-white">
      {/* Header */}
      <section className="bg-[#0B1B3D]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <span className="section-label text-[#D4AF37]">Services &amp; Pricing</span>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Professional document preparation, clearly priced.
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-300">
            Every document is prepared at your direction, based solely on the information you
            provide. Choose a service and check out securely with PayPal.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 pt-10 sm:px-6 lg:px-8">
        <ReassuranceLine testId="services-reassurance-top" />
        <p
          data-testid="services-purchase-notice"
          className="mt-4 rounded-md border border-[#D4AF37]/40 bg-[#F8FAFC] px-5 py-4 text-center text-sm leading-relaxed text-[#0B1B3D]"
        >
          Resumes and letters can be purchased instantly. For all other services, submit a request
          and we'll email you a secure payment link after confirming your details.
        </p>
      </div>

      {/* Service groups */}
      {SERVICE_GROUPS.map((group) => (
        <section key={group.group} className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center gap-4">
            <h2 className="font-serif text-2xl font-bold text-[#0B1B3D] sm:text-3xl">
              {group.group}
            </h2>
            <span className="h-px flex-1 bg-slate-200" />
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {group.services.map((service, i) => (
              <ServiceCard key={service.id} service={service} index={i} />
            ))}
          </div>
        </section>
      ))}

      {/* Detailed descriptions */}
      <section className="bg-[#F8FAFC]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="section-label text-[#D4AF37]">How These Services Work</span>
            <h2 className="mt-4 font-serif text-3xl font-bold text-[#0B1B3D] sm:text-4xl">
              Eviction, Traffic &amp; Debt — explained plainly.
            </h2>
          </div>
          <div className="mt-12 grid gap-8 lg:grid-cols-3">
            {DETAILED_DESCRIPTIONS.map((d) => (
              <motion.div
                key={d.title}
                initial={FADE_INITIAL}
                whileInView={FADE_IN_VIEW}
                viewport={VIEWPORT_ONCE}
                transition={{ duration: 0.45 }}
                className="rounded-lg border border-slate-200 bg-white p-8"
              >
                <h3 className="font-serif text-xl font-semibold text-[#0B1B3D]">{d.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-600">{d.body}</p>
                <div className="mt-5 flex items-start gap-2 rounded-md bg-[#F8FAFC] p-3 text-xs font-medium text-[#0B1B3D] ring-1 ring-slate-200">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#D4AF37]" />
                  <span>{d.disclosure}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Full legal disclosure */}
      <section className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8">
        <LegalDisclosure />
      </section>
    </div>
  );
}
